import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import {
  ArrowLeft,
  Download,
  Eye,
  Loader2,
  Rocket,
  Save,
  Wand2
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  createDocIntelligenceTemplateFromReview,
  downloadDocument,
  fixDocIntelligenceTemplateSampleLeaks,
  generateDocIntelligenceTemplate,
  getClosedDealsByProjectId,
  getCompanyByID,
  getDocIntelligenceTemplate,
  getDocIntelligenceTemplateSampleLeaks,
  getProjectByID,
  previewDocIntelligenceTemplate,
  promoteDocIntelligenceTemplate,
  rebuildDocIntelligenceTemplateContent,
  sanitizeDocIntelligenceTemplate,
  updateDocIntelligenceTemplate
} from '../../services/api/implementation/impl';
import { btnGhost, btnPrimary, btnSecondary, DocIntelPanelHeader, DocIntelSkeleton } from './DocIntelUi';
import { extractPlaceholderNames, getFieldLabel, stablePlaceholderList } from './docIntelUtils';
import { suggestMappingForPlaceholder } from './mappingRules';
import DocIntelRichTemplateEditor from './DocIntelRichTemplateEditor';
import DocIntelStudioPreview from './DocIntelStudioPreview';
import DocIntelStudioSidebar from './DocIntelStudioSidebar';

const COMPUTED_PRICING_FIELDS = [
  'unit.price.base',
  'unit.gst_percent',
  'sale.gst_amount',
  'sale.total',
  'sale.total.words',
  'sale.base.words'
];

const DocIntelTemplatePanel = ({
  dealId,
  reviewSessionId,
  meta,
  templateId: initialTemplateId,
  onTemplateCreated,
  onBack,
  studioMode = false,
  projectId,
  onPromoted,
  onOpenReview
}) => {
  const queryClient = useQueryClient();
  const [templateId, setTemplateId] = useState(initialTemplateId);
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [status, setStatus] = useState('draft');
  const [linkedTemplateId, setLinkedTemplateId] = useState(null);
  const [sourceReviewSessionId, setSourceReviewSessionId] = useState(
    reviewSessionId || null
  );
  const [fieldMappings, setFieldMappings] = useState([]);
  const [includeLogo, setIncludeLogo] = useState(true);
  const [includeWatermark, setIncludeWatermark] = useState(false);
  const [templateTables, setTemplateTables] = useState([]);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [filledHtml, setFilledHtml] = useState(null);
  const [viewMode, setViewMode] = useState('tags');
  const [previewDealId, setPreviewDealId] = useState(dealId || null);
  const [highlightLeakValue, setHighlightLeakValue] = useState(null);
  const [activeField, setActiveField] = useState(null);
  const [locateField, setLocateField] = useState(null);
  const [promoteChecks, setPromoteChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sampleLeaks, setSampleLeaks] = useState([]);
  const [autoFixable, setAutoFixable] = useState([]);
  const [entityAudit, setEntityAudit] = useState(null);
  const [previewAccent, setPreviewAccent] = useState(null);
  const [projectStaticChecklist, setProjectStaticChecklist] = useState(null);
  const [projectStaticConfirmedAt, setProjectStaticConfirmedAt] = useState(null);
  const initRef = useRef(false);

  const companyId =
    typeof window !== 'undefined'
      ? localStorage.getItem('companyId') ||
        Cookies.get('companyId') ||
        Cookies.get('company_id') ||
        ''
      : '';

  const companyQuery = useQuery({
    queryKey: ['docIntelCompany', companyId],
    queryFn: () => getCompanyByID(companyId),
    enabled: Boolean(studioMode && companyId),
    staleTime: 5 * 60 * 1000
  });

  const projectQuery = useQuery({
    queryKey: ['docIntelProject', projectId],
    queryFn: () => getProjectByID(projectId),
    enabled: Boolean(studioMode && projectId),
    staleTime: 5 * 60 * 1000
  });

  const closedDealsQuery = useQuery({
    queryKey: ['docIntelClosedDeals', projectId],
    queryFn: () => getClosedDealsByProjectId(projectId),
    enabled: Boolean(studioMode && projectId),
    staleTime: 5 * 60 * 1000
  });

  const closedDeals = useMemo(() => {
    const raw = closedDealsQuery.data;
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.data)) return raw.data;
    if (Array.isArray(raw?.closed_deals)) return raw.closed_deals;
    return [];
  }, [closedDealsQuery.data]);

  const branding = useMemo(() => {
    const company = companyQuery.data || {};
    const project = projectQuery.data || {};
    const accent =
      previewAccent ||
      company.accent_color ||
      '#0A6C5A';
    const logoArr = company.company_logo;
    const logoUrl = Array.isArray(logoArr)
      ? logoArr[0]?.url || null
      : company.logo || company.logo_url || null;
    return {
      accentColor: accent,
      companyName: company.name || company.company_name || '',
      projectName: project.name || project.project_name || '',
      address: company.address || company.registered_address || '',
      email: company.email || '',
      phone: company.phone || company.mobile || company.number || '',
      gstin: company.gst || company.gstin || company.gst_number || '',
      logoUrl,
      includeLogo
    };
  }, [companyQuery.data, projectQuery.data, previewAccent, includeLogo]);

  const handleMappingsChange = useCallback((rows) => {
    setFieldMappings(rows);
  }, []);

  const refreshSampleLeaks = useCallback(
    async (id) => {
      if (!id || !studioMode) return;
      try {
        const scan = await getDocIntelligenceTemplateSampleLeaks(id);
        setSampleLeaks(scan?.leaks ?? []);
        setAutoFixable(scan?.auto_fixable ?? []);
        setEntityAudit(scan?.entity_audit ?? null);
      } catch {
        /* advisory only */
      }
    },
    [studioMode]
  );

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const load = async () => {
      try {
        if (initialTemplateId) {
          let tpl = await getDocIntelligenceTemplate(initialTemplateId);
          const isDraft = (tpl.status || 'draft') === 'draft' && !tpl.linked_template_id;
          // Persist scrub into Tags view (not only at PDF generate).
          if (isDraft && studioMode) {
            try {
              const sanitized = await sanitizeDocIntelligenceTemplate(tpl.id);
              if (sanitized?.definition) tpl = sanitized.definition;
              if (sanitized?.actions?.length) {
                toast.success(
                  `Cleaned ${sanitized.actions.length} sample leftover${sanitized.actions.length === 1 ? '' : 's'} from template`
                );
              }
            } catch {
              /* non-blocking — editor still loads */
            }
          }
          setTemplateId(tpl.id);
          setName(tpl.name || '');
          setContent(tpl.content || '');
          setStatus(tpl.status || 'draft');
          setLinkedTemplateId(tpl.linked_template_id ?? null);
          setSourceReviewSessionId(
            tpl.source_review_session_id ?? reviewSessionId ?? null
          );
          setFieldMappings(tpl.field_mappings ?? []);
          setIncludeLogo(tpl.include_logo !== false);
          setIncludeWatermark(Boolean(tpl.include_watermark));
          setTemplateTables(tpl.template_tables ?? []);
          setProjectStaticChecklist(tpl.project_static_checklist ?? null);
          setProjectStaticConfirmedAt(tpl.project_static_confirmed_at ?? null);
          await refreshSampleLeaks(tpl.id);
        } else if (reviewSessionId) {
          const baseName =
            (meta?.subtitle || 'Agreement template').replace(/\.[^.]+$/, '') ||
            'Agreement template';
          let tpl = await createDocIntelligenceTemplateFromReview(reviewSessionId, baseName);
          if (studioMode) {
            try {
              const sanitized = await sanitizeDocIntelligenceTemplate(tpl.id);
              if (sanitized?.definition) tpl = sanitized.definition;
            } catch {
              /* create already scrubs; sanitize is best-effort */
            }
          }
          setTemplateId(tpl.id);
          setName(tpl.name || baseName);
          setContent(tpl.content || '');
          setStatus(tpl.status || 'draft');
          setSourceReviewSessionId(
            tpl.source_review_session_id ?? reviewSessionId ?? null
          );
          setFieldMappings(tpl.field_mappings ?? []);
          setIncludeLogo(tpl.include_logo !== false);
          setIncludeWatermark(Boolean(tpl.include_watermark));
          setTemplateTables(tpl.template_tables ?? []);
          setProjectStaticChecklist(tpl.project_static_checklist ?? null);
          setProjectStaticConfirmedAt(tpl.project_static_confirmed_at ?? null);
          onTemplateCreated?.(tpl.id);
          await refreshSampleLeaks(tpl.id);
        } else {
          setLoading(false);
          return;
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || 'Failed to load template');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [initialTemplateId, reviewSessionId, meta?.subtitle, onTemplateCreated, refreshSampleLeaks]);

  const placeholders = useMemo(
    () => stablePlaceholderList(extractPlaceholderNames(content)),
    [content]
  );

  const suggestedFields = useMemo(() => {
    const names = new Set(placeholders);
    for (const mapping of fieldMappings) {
      if (mapping?.placeholder_name) names.add(mapping.placeholder_name);
    }
    for (const name of COMPUTED_PRICING_FIELDS) {
      names.add(name);
    }
    for (const table of templateTables) {
      if (table?.id) names.add(`table.${table.id}`);
    }
    return stablePlaceholderList([...names]);
  }, [placeholders, fieldMappings, templateTables]);

  const isPromoted = status === 'active' || Boolean(linkedTemplateId);
  const useRichEditor = true;

  // Auto-attach known deal/DFV mappings for placeholders that lack a row
  // (e.g. salient-table fields: carpet, built-up, RERA, accommodation.tag).
  useEffect(() => {
    if (!placeholders.length || isPromoted) return;
    setFieldMappings((prev) => {
      const have = new Set(
        (prev || []).map((m) => (m.placeholder_name || '').toLowerCase())
      );
      const additions = [];
      for (const name of placeholders) {
        const key = (name || '').toLowerCase();
        if (!key || have.has(key)) continue;
        const rule = suggestMappingForPlaceholder(name);
        if (!rule?.target_type) continue;
        additions.push({
          placeholder_name: name,
          target_type: rule.target_type,
          target_path: rule.target_path,
          static_value: rule.static_value
        });
        have.add(key);
      }
      if (!additions.length) return prev;
      return [...(prev || []), ...additions];
    });
  }, [placeholders, isPromoted]);

  const filledPreviewMutation = useMutation({
    mutationFn: ({ id, deal, demo }) =>
      previewDocIntelligenceTemplate(id, deal, { demo }),
    onSuccess: (data) => {
      setFilledHtml(data?.html || '');
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Filled preview failed');
    }
  });

  useEffect(() => {
    if (!studioMode || viewMode !== 'filled' || !templateId) return;

    let cancelled = false;
    (async () => {
      try {
        if (!isPromoted) {
          // Persist local edits so filled preview matches what the user sees in Tags
          await updateDocIntelligenceTemplate(templateId, {
            name: name.trim() || undefined,
            content,
            field_mappings: fieldMappings,
            template_tables: templateTables,
            include_logo: includeLogo,
            include_watermark: includeWatermark
          });
        }
        if (cancelled) return;
        const deal = previewDealId || dealId || null;
        filledPreviewMutation.mutate({
          id: templateId,
          deal,
          demo: !deal
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(
          err?.response?.data?.message || err?.message || 'Could not refresh filled preview'
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [studioMode, viewMode, templateId, previewDealId, dealId, isPromoted]);

  const insertFieldToken = useCallback(
    (placeholderOrToken) => {
      if (status === 'active' || linkedTemplateId) return;
      const token = placeholderOrToken.includes('{{')
        ? placeholderOrToken
        : `{{ ${placeholderOrToken} }}`;
      setContent((prev) => {
        const base = prev || '';
        if (!base.trim()) return `<p>${token}</p>`;
        if (base.includes(token)) return base;
        return `${base}<p>${token}</p>`;
      });
      setViewMode('tags');
      toast.success('Field added to the document');
    },
    [status, linkedTemplateId]
  );

  const saveMutation = useMutation({
    mutationFn: () =>
      updateDocIntelligenceTemplate(templateId, {
        name: name.trim() || undefined,
        content,
        field_mappings: fieldMappings,
        template_tables: templateTables,
        include_logo: includeLogo,
        include_watermark: includeWatermark
      }),
    onSuccess: async (tpl) => {
      const definition = tpl?.definition ?? tpl;
      setStatus(definition.status || status);
      if (typeof definition.content === 'string') {
        setContent(definition.content);
      }
      if (definition.field_mappings) setFieldMappings(definition.field_mappings);
      setProjectStaticChecklist(definition.project_static_checklist ?? null);
      setProjectStaticConfirmedAt(definition.project_static_confirmed_at ?? null);
      if (Array.isArray(tpl?.sample_leaks)) {
        setSampleLeaks(tpl.sample_leaks);
        setAutoFixable([]);
      } else if (Array.isArray(definition?.sample_leaks)) {
        setSampleLeaks(definition.sample_leaks);
        setAutoFixable([]);
      } else {
        await refreshSampleLeaks(templateId);
      }
      queryClient.invalidateQueries({ queryKey: ['docIntelTemplateVersions', templateId] });
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: ['docIntelTemplates', projectId] });
      }
      toast.success('Template saved');
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Save failed');
    }
  });

  const rebuildMutation = useMutation({
    mutationFn: () => rebuildDocIntelligenceTemplateContent(templateId),
    onSuccess: async (result) => {
      const definition = result?.definition ?? result;
      if (definition?.content) setContent(definition.content);
      if (definition?.field_mappings) setFieldMappings(definition.field_mappings);
      if (definition?.template_tables) setTemplateTables(definition.template_tables);
      const method = result?.method === 'llm' ? 'AI layout' : 'rules layout';
      const warningCount = result?.warnings?.length ?? 0;
      const leakCount = result?.sample_leaks?.length ?? 0;
      queryClient.invalidateQueries({ queryKey: ['docIntelTemplateVersions', templateId] });
      await refreshSampleLeaks(templateId);
      if (leakCount > 0) {
        toast.error(
          `Rebuilt (${method}) but ${leakCount} sample value(s) still hard-coded — fix before promote`
        );
      } else {
        toast.success(
          warningCount > 0
            ? `Content rebuilt (${method}) with ${warningCount} note(s)`
            : `Content rebuilt (${method})`
        );
      }
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Rebuild failed');
    }
  });

  const fixLeaksMutation = useMutation({
    mutationFn: (opts = {}) => fixDocIntelligenceTemplateSampleLeaks(templateId, opts),
    onSuccess: async (result, vars) => {
      const definition = result?.definition ?? result;
      if (definition?.content) setContent(definition.content);
      if (definition?.field_mappings) setFieldMappings(definition.field_mappings);
      const fixed = result?.retokenized?.length ?? 0;
      const remaining = result?.remaining_leaks?.length ?? 0;
      const skipped = result?.skipped?.length ?? vars?.skipCount ?? 0;
      setSampleLeaks(result?.remaining_leaks ?? []);
      setAutoFixable([]);
      setPromoteChecks([]);
      queryClient.invalidateQueries({ queryKey: ['docIntelTemplateVersions', templateId] });
      toast.success(
        `${fixed} field${fixed === 1 ? '' : 's'} fixed${skipped ? `, ${skipped} skipped` : ''}` +
          (remaining ? ` · ${remaining} still need a look` : '')
      );
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Auto-fix failed');
    }
  });

  const previewMutation = useMutation({
    mutationFn: () => previewDocIntelligenceTemplate(templateId, dealId),
    onSuccess: (data) => {
      setPreviewHtml(data?.html || '');
      toast.success('Preview updated');
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Preview failed');
    }
  });

  const promoteMutation = useMutation({
    mutationFn: async ({ force } = {}) => {
      await updateDocIntelligenceTemplate(templateId, {
        name: name.trim() || undefined,
        content,
        field_mappings: fieldMappings,
        include_logo: includeLogo,
        include_watermark: includeWatermark
      });
      return promoteDocIntelligenceTemplate(templateId, { force });
    },
    onSuccess: (result) => {
      setStatus(result?.definition?.status || 'active');
      setLinkedTemplateId(result?.definition?.linked_template_id ?? result?.template?.id ?? null);
      setSampleLeaks([]);
      setPromoteChecks(result?.checks ?? []);
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: ['docIntelTemplates', projectId] });
      }
      toast.success(
        studioMode
          ? 'Template promoted — available under Property Documents'
          : 'Promoted to Property Documents'
      );
      onPromoted?.(result);
    },
    onError: (err) => {
      const data = err?.response?.data;
      const checks = data?.checks;
      const leaks = data?.sample_leaks;
      if (Array.isArray(checks) && checks.length) {
        setPromoteChecks(checks);
      }
      if (Array.isArray(leaks) && leaks.length) {
        setSampleLeaks(leaks);
      }
      toast.error(data?.message || err?.message || 'Promote failed');
    }
  });

  const requestPromote = () => {
    const failed = promoteChecks.filter((c) => !c.ok);
    const critical = sampleLeaks.filter((l) => l.severity === 'critical');
    if (failed.length > 0 || critical.length > 0) {
      const lines = [
        failed.length
          ? `${failed.length} promote check(s) still failing.`
          : `${critical.length} hard-coded sample value(s) remain.`,
        '',
        'They can show up wrong on every deal PDF.',
        '',
        'OK = force promote anyway (admins). Cancel = fix first.'
      ];
      const ok = window.confirm(lines.join('\n'));
      if (!ok) return;
      promoteMutation.mutate({ force: true });
      return;
    }
    const ok = window.confirm(
      'Promote this template to Live?\n\nClosed deals will use it for agreement PDFs. You can still edit drafts separately.'
    );
    if (!ok) return;
    setPromoteChecks([]);
    promoteMutation.mutate({ force: false });
  };

  const generateMutation = useMutation({
    mutationFn: () => generateDocIntelligenceTemplate(templateId, dealId),
    onSuccess: async (result) => {
      const docId = result?.document_id;
      if (!docId) {
        toast.success('Document generated');
        return;
      }
      try {
        const data = await downloadDocument(docId);
        const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `${name || 'document'}_${docId}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('PDF downloaded');
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || 'Download failed');
      }
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Generate failed');
    }
  });

  const isBusy =
    saveMutation.isPending ||
    rebuildMutation.isPending ||
    fixLeaksMutation.isPending ||
    previewMutation.isPending ||
    filledPreviewMutation.isPending ||
    promoteMutation.isPending ||
    generateMutation.isPending;

  const criticalLeaks = sampleLeaks.filter((l) => l.severity === 'critical');
  const failedPromoteChecks = promoteChecks.filter((c) => !c.ok);

  const previewDealLabel = useMemo(() => {
    if (previewDealId || dealId) {
      const id = previewDealId || dealId;
      const deal = closedDeals.find((d) => Number(d.id) === Number(id));
      const label =
        deal?.unit_name ||
        deal?.Unit?.name ||
        deal?.applicant_name ||
        deal?.Application?.name ||
        `Deal #${id}`;
      return `Filled with deal: ${label}`;
    }
    return 'Filled with demo buyer & pricing';
  }, [previewDealId, dealId, closedDeals]);

  if (loading) {
    return <DocIntelSkeleton rows={4} />;
  }

  const renderContentEditor = () => {
    if (useRichEditor) {
      return (
        <DocIntelRichTemplateEditor
          value={content}
          onChange={setContent}
          disabled={isBusy || isPromoted}
          suggestedFields={suggestedFields}
          hideFieldPalette={studioMode}
        />
      );
    }

    return (
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={14}
        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs leading-relaxed text-slate-800"
        spellCheck={false}
      />
    );
  };

  return (
    <div className="flex min-h-0 flex-col">
      <DocIntelPanelHeader
        title={meta?.title || 'Agreement template'}
        subtitle={meta?.subtitle}
        hint={meta?.hint}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {!studioMode && (autoFixable.length > 0 || criticalLeaks.length > 0) && templateId && !isPromoted ? (
              <button
                type="button"
                disabled={isBusy || !templateId}
                onClick={() => fixLeaksMutation.mutate()}
                className={btnSecondary}
                title="Replace hard-coded sample Aadhaar/PAN/names with {{ placeholders }}">
                {fixLeaksMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Wand2 className="h-4 w-4 text-amber-700" />
                )}
                Convert sample values
              </button>
            ) : null}
            {onBack ? (
              <button type="button" onClick={onBack} className={btnGhost}>
                <ArrowLeft className="h-4 w-4" />
                {studioMode ? 'Back to uploads' : 'Back to apply'}
              </button>
            ) : null}
          </div>
        }
      />

      {studioMode ? (
        <div className="grid items-start gap-3 p-3 sm:p-4 lg:grid-cols-[minmax(0,1fr)_300px] xl:grid-cols-[minmax(0,1fr)_320px]">
          <div className="min-w-0 space-y-3">
            {failedPromoteChecks.length > 0 && !isPromoted ? (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs text-rose-950">
                <p className="font-semibold">Fix before promote</p>
                <ul className="mt-1.5 list-disc space-y-0.5 pl-4">
                  {failedPromoteChecks.map((c) => (
                    <li key={c.id}>
                      {c.message}
                      {c.field ? (
                        <span className="ml-1 text-rose-700/70">
                          ({getFieldLabel(c.field)})
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {closedDeals.length > 0 && viewMode === 'filled' ? (
              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                <label className="font-medium">Preview with deal</label>
                <select
                  value={previewDealId || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPreviewDealId(v ? Number(v) : null);
                    setFilledHtml(null);
                  }}
                  className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs">
                  <option value="">Demo sample</option>
                  {closedDeals.slice(0, 40).map((d) => (
                    <option key={d.id} value={d.id}>
                      #{d.id}
                      {d.unit_name || d.Unit?.name
                        ? ` · ${d.unit_name || d.Unit?.name}`
                        : ''}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className={btnGhost}
                  disabled={isBusy}
                  onClick={() => {
                    const deal = previewDealId || dealId || null;
                    filledPreviewMutation.mutate({
                      id: templateId,
                      deal,
                      demo: !deal
                    });
                  }}>
                  Refresh filled
                </button>
              </div>
            ) : null}
            <DocIntelStudioPreview
              content={content}
              filledHtml={filledHtml}
              filledPending={filledPreviewMutation.isPending}
              viewMode={isPromoted && viewMode === 'edit' ? 'tags' : viewMode}
              onViewModeChange={(mode) => {
                if (isPromoted && mode === 'edit') return;
                setViewMode(mode);
                if (mode === 'filled') setFilledHtml(null);
              }}
              templateName={name}
              branding={branding}
              fieldMappings={fieldMappings}
              sampleLeaks={sampleLeaks}
              templateTables={templateTables}
              onDropField={insertFieldToken}
              disabled={isBusy || isPromoted}
              highlightValue={highlightLeakValue}
              highlightField={locateField}
              onVisibleFieldChange={setActiveField}
              previewDealLabel={previewDealLabel}
              editSlot={renderContentEditor()}
            />
          </div>
          <DocIntelStudioSidebar
            name={name}
            onNameChange={setName}
            branding={branding}
            onBrandingChange={(patch) => {
              if (patch.accentColor) setPreviewAccent(patch.accentColor);
            }}
            includeLogo={includeLogo}
            includeWatermark={includeWatermark}
            onIncludeLogoChange={setIncludeLogo}
            onIncludeWatermarkChange={setIncludeWatermark}
            placeholders={placeholders}
            fieldMappings={fieldMappings}
            sampleLeaks={sampleLeaks}
            entityAudit={entityAudit}
            templateTables={templateTables}
            onMappingsChange={handleMappingsChange}
            onInsertField={insertFieldToken}
            activeField={activeField}
            onHoverField={setActiveField}
            onLocateInDocument={(name) => {
              setViewMode('tags');
              setLocateField(null);
              window.requestAnimationFrame(() => setLocateField(name));
            }}
            onOpenReview={
              onOpenReview
                ? () => onOpenReview(sourceReviewSessionId || reviewSessionId)
                : undefined
            }
            onFixLeaks={(opts) => fixLeaksMutation.mutate(opts || {})}
            onHighlightLeak={(leak) => {
              setViewMode('tags');
              setHighlightLeakValue(null);
              window.requestAnimationFrame(() => {
                setHighlightLeakValue(leak?.value || null);
              });
            }}
            fixLeaksPending={fixLeaksMutation.isPending}
            onRebuild={() => rebuildMutation.mutate()}
            rebuildPending={rebuildMutation.isPending}
            templateId={templateId}
            projectId={projectId}
            isPromoted={isPromoted}
            isBusy={isBusy}
            projectStaticChecklist={projectStaticChecklist}
            projectStaticConfirmedAt={projectStaticConfirmedAt}
            onProjectStaticConfirmed={(template) => {
              setProjectStaticChecklist(template.project_static_checklist ?? null);
              setProjectStaticConfirmedAt(template.project_static_confirmed_at ?? null);
              setPromoteChecks((prev) =>
                prev.map((c) =>
                  c.id === 'PROJECT_STATIC_CHECKLIST_CONFIRMED' ? { ...c, ok: true } : c
                )
              );
            }}
            onRestored={(template) => {
              setName(template.name || '');
              setContent(template.content || '');
              setFieldMappings(template.field_mappings ?? []);
              setStatus(template.status || 'draft');
              setLinkedTemplateId(template.linked_template_id ?? null);
              setProjectStaticChecklist(template.project_static_checklist ?? null);
              setProjectStaticConfirmedAt(template.project_static_confirmed_at ?? null);
            }}
          />
        </div>
      ) : (
        <div className="grid gap-4 p-4 sm:p-5 lg:grid-cols-2">
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-600">
                Template name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-600">
                {useRichEditor ? 'Agreement body' : 'Content (HTML + placeholders)'}
              </label>
              {renderContentEditor()}
            </div>

            {!useRichEditor && placeholders.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-600">
                  Placeholders
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {placeholders.map((ph) => (
                    <code
                      key={ph}
                      className="rounded-md bg-teal-50 px-2 py-0.5 text-xs font-semibold text-teal-900 ring-1 ring-teal-100">
                      {`{{ ${ph} }}`}
                    </code>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex min-h-[280px] flex-col rounded-xl border border-slate-200 bg-slate-50/40">
            <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
              <p className="text-xs font-medium text-slate-600">Deal preview</p>
              <button
                type="button"
                disabled={isBusy || !templateId || !dealId}
                onClick={() => previewMutation.mutate()}
                className={btnSecondary}>
                {previewMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
                Refresh preview
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto p-3">
              {previewHtml ? (
                <div
                  className="prose prose-sm max-w-none rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-100"
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              ) : (
                <p className="py-12 text-center text-sm text-slate-500">
                  Run preview to see merged deal data in this template.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-auto flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-4 py-3 sm:px-5">
        <p className="text-sm text-slate-600">
          Template:{' '}
          <span
            className={`inline-flex rounded-md px-1.5 py-0.5 text-xs font-bold uppercase tracking-wide ${
              isPromoted
                ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200'
                : 'bg-amber-50 text-amber-900 ring-1 ring-amber-200'
            }`}>
            {isPromoted ? 'Live' : 'Draft'}
          </span>
          {isPromoted && ' · used on closed-deal PDFs'}
          {studioMode && !isPromoted && placeholders.length > 0 && ` · ${placeholders.length} fields`}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={isBusy || !templateId}
            onClick={() => saveMutation.mutate()}
            className={btnSecondary}>
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save draft
          </button>
          {!isPromoted && (
            <button
              type="button"
              disabled={isBusy || !templateId}
              onClick={requestPromote}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-teal-800 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-900 focus:outline-none focus:ring-2 focus:ring-teal-800/40 disabled:cursor-not-allowed disabled:opacity-50"
              title="Publish this template for closed deals">
              {promoteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Rocket className="h-4 w-4" />
              )}
              Promote to live
            </button>
          )}
          {!studioMode && (
            <button
              type="button"
              disabled={isBusy || !templateId || !isPromoted}
              onClick={() => generateMutation.mutate()}
              className={btnPrimary}
              title={!isPromoted ? 'Promote template first' : undefined}>
              {generateMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Generate PDF
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocIntelTemplatePanel;
